import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Ban, RefreshCw } from 'lucide-react';
import { Badge, Button } from '../../shared/ui';
import {
  avisDecisionSpam, CLASSE_SPAM_POSSIBLE, decisionSpamAttendue, estSpamPossible, LIBELLE_SPAM_POSSIBLE, trancherSpam,
  type DecisionSpam,
} from './spam';

/** Badge « Spam possible » : tableau, cartes mobiles et fiche. Rien si le
 *  dossier ne porte pas la marque. */
export const BadgeSpamPossible: React.FC<{ dossier: unknown; className?: string }> = ({ dossier, className = '' }) => {
  if (!estSpamPossible(dossier)) return null;
  return (
    <Badge className={`${CLASSE_SPAM_POSSIBLE} ${className}`}>
      <AlertTriangle className="w-3 h-3" aria-hidden="true" />
      {LIBELLE_SPAM_POSSIBLE}
    </Badge>
  );
};

// Cibles tactiles d'au moins 40 px sous 1024 px, boutons pleine largeur sur téléphone.
const BOUTON = 'max-lg:min-h-10 max-sm:w-full max-sm:justify-center';

/**
 * Bandeau de la fiche d'un dossier « spam possible » non clos : explique
 * pourquoi rien n'est parti vers l'expéditeur, et propose de trancher.
 * « Spam » demande confirmation (le dossier est clos). Après la décision,
 * `surDecision` reçoit les champs modifiés, à fusionner dans le dossier
 * affiché ; la fiche relit ensuite le dossier complet.
 */
export const BandeauSpamPossible: React.FC<{
  dossier: { id: string; status?: string; spamPossible?: boolean } | object;
  surDecision: (maj: Record<string, unknown>, avis: string) => void;
}> = ({ dossier, surDecision }) => {
  const [enCours, setEnCours] = useState<DecisionSpam | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  if (!decisionSpamAttendue(dossier)) return null;
  const id = (dossier as { id: string }).id;

  const trancher = async (decision: DecisionSpam) => {
    if (decision === 'spam' && !window.confirm('Clore ce dossier comme spam ? Aucune notification ne sera envoyée.')) return;
    setEnCours(decision);
    setErreur(null);
    try {
      const r = await trancherSpam(id, decision);
      const maj: Record<string, unknown> = decision === 'spam'
        ? { spamPossible: true, status: 'CLOSED' }
        : {
            spamPossible: false,
            ...('contactEmail' in r && r.contactEmail ? { contactEmail: r.contactEmail } : {}),
          };
      surDecision(maj, avisDecisionSpam(decision, r as { contactEmail?: string | null; deplacement?: string }));
    } catch (e) {
      setErreur(e instanceof Error && e.message ? e.message : 'La décision n\'a pas pu être enregistrée.');
    } finally {
      setEnCours(null);
    }
  };

  return (
    <div role="region" aria-label="Spam possible" className="mt-3 p-3 rounded-lg border border-[#F2C4BF] bg-[#FBE9E7] text-[#8A1C16]">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-0 text-sm">
          <p className="font-semibold">Spam possible</p>
          <p className="mt-0.5">
            Microsoft a classé cet e-mail en courrier indésirable. Rien n'a été envoyé à l'expéditeur
            (ni accusé de réception, ni notification) et le dossier n'est relié à aucun client. Vérifiez-le, puis tranchez.
          </p>
          {erreur && <p role="alert" className="mt-1 font-medium">{erreur}</p>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 max-sm:flex-col">
        <Button variant="primary" size="sm" className={BOUTON} disabled={enCours !== null} onClick={() => trancher('pas-spam')}>
          {enCours === 'pas-spam' ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
          Pas un spam
        </Button>
        <Button variant="danger" size="sm" className={BOUTON} disabled={enCours !== null} onClick={() => trancher('spam')}>
          {enCours === 'spam' ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
          Spam : clore
        </Button>
      </div>
    </div>
  );
};
